import React, { useState, useEffect, useCallback, FC } from 'react';
import Drawer from '@material-ui/core/Drawer';
import { useSelector } from 'react-redux';
import { ApolloError } from '@apollo/client';

import { useImpactAiPresetsQuery } from 'generated/graphql';
import { displayName } from 'utils/formatFullName';
import { selectUser } from 'entities/user/selectors';

import { useStyles } from './styles';
import PresetSelection from './components/PresetSelection';
import ChatHeader from './components/ChatHeader';
import { Preset } from './types';
import ImpactAiChat from './components/ImpactAiChat';
import { MethodProvider } from './hooks/ImpactAiMethodProvider';
import { ImpactAiContextProvider, useImpactAiContext } from './hooks/ImpactAiContextProvider';
import { useImpactAiTracking } from './hooks/useImpactAiTracking';

interface ImpactAiDrawerProps {
  isOpen?: boolean;
  onDrawerOpen?: (isOpen: boolean) => void;
}

interface DrawerContentProps {
  isOpen: boolean;
  onDrawerOpen?: (isOpen: boolean) => void;
  tracker: ReturnType<typeof useImpactAiTracking>;
  presetsData: { impactAiPresets?: { items?: Preset[] } };
  presetsLoading: boolean;
  presetsError: ApolloError | undefined;
}

const DrawerContent: FC<DrawerContentProps> = ({
  isOpen,
  onDrawerOpen,
  tracker,
  presetsData,
  presetsLoading,
  presetsError,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(isOpen);
  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null);
  const [actionMode, setActionMode] = useState(false);

  const { resetContext } = useImpactAiContext();

  const allPresets: Preset[] = (presetsData?.impactAiPresets?.items as Preset[]) || [];
  const enabledPresets = allPresets.filter((preset) => preset.enabled !== false);

  useEffect(() => {
    setOpen(isOpen);
    tracker.trackDrawer(isOpen);
  }, [isOpen, tracker]);

  const toggleDrawer = useCallback(() => {
    const newOpenState = !open;
    setOpen(newOpenState);
    onDrawerOpen?.(newOpenState);

    // Reset context when closing the drawer
    if (open && !newOpenState) {
      resetContext();
      setActionMode(false);
      setSelectedPreset(null);
    }
  }, [open, onDrawerOpen, resetContext]);

  const handlePresetSelect = useCallback(
    (preset: Preset) => {
      // Reset context before selecting a new preset to ensure clean state
      resetContext();
      // Immediately switch to action mode (non-blocking)
      setSelectedPreset(preset);
      setActionMode(true);
      tracker.startPresetSession(preset.id);
      tracker.trackPresetSelect(preset);
    },
    [tracker, resetContext]
  );

  const handelBackToPresets = useCallback(() => {
    // Reset context when going back to preset selection
    resetContext();
    setActionMode(false);
    setSelectedPreset(null);
    tracker.endPresetSession();
    tracker.trackBackToPresets();
  }, [tracker, resetContext]);

  return (
    <MethodProvider>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={open}
        classes={{ paper: classes.drawerPaper }}
        BackdropProps={{ invisible: true }}
      >
        <div
          style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '200px', zIndex: 1 }}
        >
          <ChatHeader
            isActionMode={actionMode}
            onBackToPresets={handelBackToPresets}
            onClose={toggleDrawer}
          />
        </div>

        {!actionMode ? (
          <PresetSelection
            onSelectPreset={handlePresetSelect}
            presets={enabledPresets}
            loading={presetsLoading}
            error={presetsError}
          />
        ) : selectedPreset ? (
          <ImpactAiChat selectedPreset={selectedPreset} tracker={tracker} allPresets={allPresets} />
        ) : null}
      </Drawer>
    </MethodProvider>
  );
};

const ImpactAiDrawer: FC<ImpactAiDrawerProps> = ({ onDrawerOpen, isOpen = false }) => {
  const tracker = useImpactAiTracking();
  const user = useSelector(selectUser);

  // Load all presets without expansion for dynamic handling
  const {
    data: presetsData = { impactAiPresets: { items: [] } },
    loading: presetsLoading,
    error: presetsError,
  } = useImpactAiPresetsQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  // Only require user to be loaded initially, other data will be loaded by methods
  if (!user) {
    return null;
  }

  return (
    <ImpactAiContextProvider
      preset={null}
      tracker={tracker}
      initialContext={{
        userName: displayName(user.toJS()),
        userEmail: user.email,
        userId: user.id,
        userType: user.type,
        organization: user.organization
          ? {
              id: user.organization.id,
              name: user.organization.name,
              type: user.organization.type,
            }
          : null,
        currentDate: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }}
    >
      <DrawerContent
        isOpen={isOpen}
        onDrawerOpen={onDrawerOpen}
        tracker={tracker}
        presetsData={presetsData}
        presetsLoading={presetsLoading}
        presetsError={presetsError}
      />
    </ImpactAiContextProvider>
  );
};

export default ImpactAiDrawer;
